import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import EventConfigurationContext from '@totem/components/services/threats/configuration/EventConfigurationContext';
import {
  EMPTY_HIERARCHY,
  EventCategories,
  EventCategory,
  EventConfigurationItem,
  EventConfigurationItems,
} from '@totem/components/services/threats/configuration/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import {
  EVENT_CATEGORIES_ENDPOINT,
  EVENT_CONFIGURATION_ENDPOINT,
} from '@totem/utilities/endpoints';

const EMPTY_CATEGORY: EventCategory = {
  id: '',
  name: '',
  description: '',
  createdBy: '',
  createdAt: '',
  updatedBy: '',
  updatedAt: '',
};

const EMPTY_ITEM: EventConfigurationItem = {
  id: '',
  category: '',
  hierarchy: EMPTY_HIERARCHY,
  status: 'Active',
  sequence: 100,
  ticketConfiguration: {
    create: false,
    impact: 'Medium',
    urgency: 'Medium',
    category: '',
    subCategory: '',
  },
  eventMatches: [],
  example: '',
  customerInfo: { whatItMeans: '', whyTicketRaised: '', impact: '' },
  technicianInfo: { likelyCauses: [], actions: [] },
  claimKeys: [],
  cancelsKeys: [],
  createdBy: '',
  createdAt: '',
  updatedBy: '',
  updatedAt: '',
};

type Props = {
  children: ReactNode;
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

const EventConfigurationContainer = ({ children }: Props) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<EventCategories>([]);
  const [items, setItems] = useState<EventConfigurationItems>([]);

  const [editingCategory, setEditingCategory] = useState<EventCategory | null>(
    null,
  );
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const [editingItem, setEditingItem] = useState<EventConfigurationItem | null>(
    null,
  );
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);

    Promise.all([
      fetch(EVENT_CATEGORIES_ENDPOINT, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((res) => res.json()),
      fetch(EVENT_CONFIGURATION_ENDPOINT, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((res) => res.json()),
    ])
      .then(
        ([categoryRecords, itemRecords]: [
          EventCategories,
          EventConfigurationItems,
        ]) => {
          setCategories(categoryRecords ?? []);

          setItems(itemRecords ?? []);
        },
      )
      .catch(() =>
        notification.error({ message: 'Failed to load event configuration.' }),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ---- Categories ----

  const onAddCategory = () => {
    setEditingCategory({ ...EMPTY_CATEGORY });

    setCategoryModalOpen(true);
  };

  const onEditCategory = (category: EventCategory) => {
    setEditingCategory({ ...category });

    setCategoryModalOpen(true);
  };

  const onCategoryModalClose = () => {
    setCategoryModalOpen(false);

    setEditingCategory(null);
  };

  const onSaveCategory = (category: EventCategory) => {
    const isNew = !editingCategory || !editingCategory.createdAt;
    const url = isNew
      ? EVENT_CATEGORIES_ENDPOINT
      : `${EVENT_CATEGORIES_ENDPOINT}/${category.id}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, {
      method,
      headers: authHeaders(),
      body: JSON.stringify(category),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({
          message: `Category ${isNew ? 'created' : 'updated'}.`,
        });

        onCategoryModalClose();

        refresh();
      })
      .catch(() => notification.error({ message: 'Failed to save category.' }));
  };

  const onDeleteCategory = (id: string) => {
    fetch(`${EVENT_CATEGORIES_ENDPOINT}/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Delete failed');
        notification.success({ message: 'Category deleted.' });

        refresh();
      })
      .catch(() =>
        notification.error({ message: 'Failed to delete category.' }),
      );
  };

  // ---- Configuration Items ----

  const onAddItem = () => {
    setEditingItem({ ...EMPTY_ITEM });

    setItemDrawerOpen(true);
  };

  const onEditItem = (item: EventConfigurationItem) => {
    setEditingItem({ ...item });

    setItemDrawerOpen(true);
  };

  const onItemDrawerClose = () => {
    setItemDrawerOpen(false);

    setEditingItem(null);
  };

  const onSaveItem = (item: EventConfigurationItem) => {
    const isNew = !item.id;
    const url = isNew
      ? EVENT_CONFIGURATION_ENDPOINT
      : `${EVENT_CONFIGURATION_ENDPOINT}/${item.id}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, {
      method,
      headers: authHeaders(),
      body: JSON.stringify(item),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({
          message: `Configuration item ${isNew ? 'created' : 'updated'}.`,
        });

        onItemDrawerClose();

        refresh();
      })
      .catch(() =>
        notification.error({
          message: 'Failed to save configuration item.',
        }),
      );
  };

  const onDeleteItem = (id: string) => {
    fetch(`${EVENT_CONFIGURATION_ENDPOINT}/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Delete failed');
        notification.success({ message: 'Configuration item deleted.' });

        refresh();
      })
      .catch(() =>
        notification.error({
          message: 'Failed to delete configuration item.',
        }),
      );
  };

  return (
    <EventConfigurationContext.Provider
      value={{
        loading,
        categories,
        items,

        editingCategory,
        categoryModalOpen,
        onAddCategory,
        onEditCategory,
        onCategoryModalClose,
        onSaveCategory,
        onDeleteCategory,

        editingItem,
        itemDrawerOpen,
        onAddItem,
        onEditItem,
        onItemDrawerClose,
        onSaveItem,
        onDeleteItem,
      }}
    >
      {children}
    </EventConfigurationContext.Provider>
  );
};

export default EventConfigurationContainer;
